import {
  buildExampleRecommendations,
  NO_EXAMPLE_PREFERENCES,
} from '@/components/companion/build-example-recommendations';
import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import type { BrandDiscoveryPreferences } from '@fluenceur/types';
import { SparksSolid } from 'iconoir-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanionExampleCard } from './CompanionExampleCard';

/** Slight tilt of each card, in the mock-up's order. */
export const EXAMPLE_CARD_ROTATIONS = [-5, 4, -2];
/** Horizontal offset of each card, so they read as scattered rather than listed. */
export const EXAMPLE_CARD_OFFSETS = ['0px', '56px', '18px'];

const TOAST_KEYS: Record<'creators' | 'welcome', { title: string; subtitle: string }> = {
  creators: {
    title: 'onboarding.brandPanel.creatorsToast',
    subtitle: 'settings.agents.title',
  },
  welcome: {
    title: 'onboarding.brandPanel.recommendationsTomorrow',
    subtitle: 'onboarding.brandPanel.handlesWithEssential',
  },
};

const CompanionToast = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <Flex
    align="center"
    gap="12px"
    px="14px"
    py="12px"
    borderRadius="16px"
    bg="announcement.surface"
    borderWidth="1px"
    borderColor="announcement.surfaceBorder"
  >
    <Flex
      flex="none"
      w="34px"
      h="34px"
      borderRadius="10px"
      bg="navDark.aiText"
      align="center"
      justify="center"
    >
      <SparksSolid width={17} height={17} color="var(--chakra-colors-ink)" aria-hidden />
    </Flex>
    <Box minW={0}>
      <Text fontFamily="heading" fontSize="14px" fontWeight="600" color="announcement.title">
        {title}
      </Text>
      <Text fontSize="12px" color="announcement.body">
        {subtitle}
      </Text>
    </Box>
  </Flex>
);

/**
 * The Companion's side of the panel on the creators step and the welcome page:
 * three illustrative example creators drawn from the brand's preferences (no
 * request is made, the handles are masked as on the free plan) and a toast
 * saying what the Companion does with them.
 */
export const CompanionPreview = ({
  step,
  preferences,
}: {
  step: 'creators' | 'welcome';
  preferences: BrandDiscoveryPreferences | null;
}) => {
  const { t } = useTranslation();
  const prefs = preferences ?? NO_EXAMPLE_PREFERENCES;
  const examples = useMemo(
    () => buildExampleRecommendations(prefs, (key, vars) => t(key, vars)),
    [prefs, t]
  );
  const toast = TOAST_KEYS[step];

  return (
    <Flex direction="column" justify="center" gap="24px" w="full">
      <Stack gap="14px" align="flex-start">
        {examples.map((recommendation, index) => (
          <Box key={recommendation.id} ml={EXAMPLE_CARD_OFFSETS[index]}>
            <CompanionExampleCard
              recommendation={recommendation}
              niche={prefs.nicheTags[index] ?? prefs.nicheTags[0] ?? null}
              rotation={EXAMPLE_CARD_ROTATIONS[index]}
            />
          </Box>
        ))}
      </Stack>
      <CompanionToast title={t(toast.title)} subtitle={t(toast.subtitle)} />
    </Flex>
  );
};
